<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue'
import {useI18n} from 'vue-i18n'
import {useRouter} from 'vue-router'
import {useIntervalFn, useTimeoutFn} from '@vueuse/core'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {SizeVariantType} from '@toolify/client/src/modules/ui/enum/SizeVariantType'
import {IconBehavior} from '@toolify/client/src/modules/ui/enum/IconBehavior'
import {MessageSeverityTypeToIconNameTypeMap} from '@toolify/client/src/modules/notifications/maps/MessageSeverityTypeToIconNameTypeMap'
import {ILocalMessage} from '@toolify/client/src/modules/notifications/types/ILocalMessage'
import {IMessageAction} from '@toolify/client/src/modules/notifications/types/IMessageAction'
import {MessageStateType} from '@toolify/client/src/modules/notifications/enum/MessageStateType'
import {MessageSeverityType} from '@toolify/client/src/modules/notifications/enum/MessageSeverityType'
import {MessageActionKindType} from '@toolify/client/src/modules/notifications/enum/MessageActionKindType'
import {MessageDismissReasonType} from '@toolify/client/src/modules/notifications/enum/MessageDismissReasonType'
import {
  MessageAnnouncementChannelType,
} from '@toolify/client/src/modules/notifications/enum/MessageAnnouncementChannelType'
import {useLocalMessageStore} from '@toolify/client/src/stores/LocalMessageStore/useLocalMessageStore'
import {LocalMessageStoreEventType} from '@toolify/client/src/stores/LocalMessageStore/enum/LocalMessageStoreEventType'
import {ILocalMessageStoreEvent} from '@toolify/client/src/stores/LocalMessageStore/types/ILocalMessageStoreEvent'
import {useDateFormat} from '@toolify/client/src/modules/date/composables/useDateFormat'
import {writeToClipboard} from '@toolify/client/src/modules/ui/functions/writeToClipboard'
import {CypressUniqueNameType} from '@toolify/client/cypress/types/CypressUniqueNameType'

// One local message card, styled by styles/messages/_card.scss. The markup follows the prototype,
// so buttons are native <button class="msg__btn">.
interface Props {
  message: ILocalMessage
}

const props = defineProps<Props>()

const AGE_MIN_MS = 60000
const AGE_REFRESH_MS = 30000
const BUMPED_MS = 350
const MORPHED_MS = 450
const COPIED_MS = 1500

const {t} = useI18n()
const router = useRouter()
const localMessageStore = useLocalMessageStore()
const {formatRelativeTime} = useDateFormat()
const copied = ref(false)
const {start: startCopiedReset} = useTimeoutFn(() => {
  copied.value = false
}, COPIED_MS, {immediate: false})

const cardRef = ref<HTMLElement | null>(null)
const isHovered = ref(false)
const isFocused = ref(false)
const isTouched = ref(false)
const isTextExpanded = ref(false)
const now = ref(Date.now())
const flashTimeouts = new Map<string, ReturnType<typeof setTimeout>>()
let returnFocusElement: HTMLElement | null = null
let hasTimerVariables = false
let unsubscribeFromStore: (() => void) | null = null

const titleElementId = computed(() => `${props.message.id}-title`)
const textElementId = computed(() => `${props.message.id}-text`)
const errorIdElementId = computed(() => `${props.message.id}-errid`)
const isLeaving = computed(() => props.message.state === MessageStateType.Leave)
const isVisiblyPaused = computed(() => {
  return !props.message.collapsed && (isHovered.value || isFocused.value || isTouched.value)
})

const labelledBy = computed(() => {
  return props.message.title ? `${titleElementId.value} ${textElementId.value}` : textElementId.value
})

const cardStyle = computed<Record<string, string>>(() => {
  const style: Record<string, string> = {}
  if(props.message.clampLines) {
    style['--msg-clamp-lines'] = String(props.message.clampLines)
  }
  return style
})

const progressPercent = computed(() => {
  const progress = props.message.progress
  if(!progress || progress.indeterminate) {
    return 0
  }
  return Math.round((progress.value / (progress.total || 100)) * 100)
})

const actionItems = computed(() => {
  return props.message.actions.map((action, index) => {
    return {
      action,
      index,
      href: getActionHref(action),
    }
  })
})

const createdAtIso = computed(() => new Date(props.message.createdAt).toISOString())

// persistent local cards show their age, but only once it is at least a minute
const timeLabel = computed(() => {
  if(!props.message.showAge || props.message.duration !== 0) {
    return ''
  }
  if(now.value - props.message.createdAt < AGE_MIN_MS) {
    return ''
  }
  return formatRelativeTime(new Date(props.message.createdAt))
})

const hasMeta = computed(() => {
  return !!(props.message.errorId || props.message.clampLines || timeLabel.value)
})

const countLabel = computed(() => props.message.count > 1 ? `×${props.message.count}` : '')

const isProgress = computed(() => props.message.severity === MessageSeverityType.Progress)

const {pause: pauseAgeRefresh, resume: resumeAgeRefresh} = useIntervalFn(() => {
  now.value = Date.now()
}, AGE_REFRESH_MS, {immediate: false})

watch(() => props.message.showAge && props.message.duration === 0, shouldRefreshAge => {
  now.value = Date.now()
  if(shouldRefreshAge) {
    resumeAgeRefresh()
  } else {
    pauseAgeRefresh()
  }
}, {immediate: true})

// focus leaves the card before aria-hidden lands on it (pre flush runs before this card re-renders)
watch(isLeaving, leaving => {
  if(leaving) {
    moveFocusOutOfCard()
  }
}, {flush: 'pre'})

function flag(value: boolean): '1' | '0' {
  return value ? '1' : '0'
}

function getActionHref(action: IMessageAction): string | null {
  if(action.to) {
    return router.resolve(action.to).href
  }
  if(action.kind === MessageActionKindType.Link && action.href) {
    return action.href
  }
  return null
}

function getFocusTarget(element: HTMLElement): HTMLElement {
  return element.querySelector<HTMLElement>('button, a') ?? element
}

function getActiveElement(): HTMLElement | null {
  const activeElement = document.activeElement
  return activeElement instanceof HTMLElement ? activeElement : null
}

// a click focuses the card (tabindex) or its button as well; only keyboard focus may pause the card
// or be handed over to another one, a pointer focus would outlive the pointer
function isKeyboardFocus(element: Element): boolean {
  try {
    return element.matches(':focus-visible')
  } catch {
    return true
  }
}

function writeTimerVariables() {
  const element = cardRef.value
  if(!element) {
    return
  }
  const timerState = localMessageStore.getTimerState(props.message.id)
  if(!timerState) {
    if(hasTimerVariables) {
      element.style.removeProperty('--msg-timer')
      element.style.removeProperty('--msg-timer-ms')
      hasTimerVariables = false
    }
    return
  }
  element.style.setProperty('--msg-timer', timerState.ratio.toFixed(3))
  element.style.setProperty('--msg-timer-ms', `${timerState.remainingMs}ms`)
  hasTimerVariables = true
}

// data-bumped / data-morphed restart their animation: remove, reflow, set again
function flashAttribute(attribute: string, durationMs: number) {
  const element = cardRef.value
  if(!element) {
    return
  }
  const timeout = flashTimeouts.get(attribute)
  if(timeout) {
    clearTimeout(timeout)
  }
  element.removeAttribute(attribute)
  void element.offsetWidth
  element.setAttribute(attribute, '1')
  flashTimeouts.set(attribute, setTimeout(() => {
    element.removeAttribute(attribute)
    flashTimeouts.delete(attribute)
  }, durationMs))
}

function setHovered(value: boolean) {
  isHovered.value = value
  localMessageStore.setInteraction(props.message.id, {hovered: value})
}

function setFocused(value: boolean) {
  isFocused.value = value
  localMessageStore.setInteraction(props.message.id, {focused: value})
}

function setTouched(value: boolean) {
  isTouched.value = value
  localMessageStore.setInteraction(props.message.id, {touched: value})
}

function onFocusIn(event: FocusEvent) {
  const target = event.target instanceof Element ? event.target : null
  setFocused(!!target && isKeyboardFocus(target))
}

function onFocusOut(event: FocusEvent) {
  const nextTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null
  if(!cardRef.value?.contains(nextTarget)) {
    setFocused(false)
  }
}

function onPointerDown(event: PointerEvent) {
  if(event.pointerType === 'touch') {
    setTouched(true)
  }
}

function onPointerEnd() {
  if(isTouched.value) {
    setTouched(false)
  }
}

// touch has its own pause (touched): a tap sends a compat mouseenter and no mouseleave until the next tap
function onPointerEnter(event: PointerEvent) {
  if(event.pointerType !== 'touch') {
    setHovered(true)
  }
}

function onPointerLeave(event: PointerEvent) {
  if(event.pointerType !== 'touch') {
    setHovered(false)
  }
  onPointerEnd()
}

// Keyboard: the newest other visible card takes the focus, then the element focused before the push,
// then nothing. A mouse close only drops the focus, so no neighbouring card gets it (and a pause).
function moveFocusOutOfCard() {
  const element = cardRef.value
  const activeElement = getActiveElement()
  if(!element || !activeElement || !element.contains(activeElement)) {
    return
  }
  if(!isKeyboardFocus(activeElement)) {
    activeElement.blur()
    setFocused(false)
    return
  }
  const nextId = localMessageStore.getNewestVisibleMessageId(props.message.id)
  const nextCard = nextId
    ? element.parentElement?.querySelector<HTMLElement>(`.msg[data-msg-id="${CSS.escape(nextId)}"]`)
    : null
  const target = nextCard ? getFocusTarget(nextCard) : (returnFocusElement?.isConnected ? returnFocusElement : null)
  if(target) {
    target.focus({preventScroll: true})
  } else {
    activeElement.blur()
  }
  setFocused(false)
}

// After a morph the focus returns to the new content only for keyboard users; a mouse click on
// "Undo" focuses the button, and keeping that focus would pause the morphed card forever
function onMorph() {
  const element = cardRef.value
  if(!element) {
    return
  }
  const activeElement = getActiveElement()
  const hadFocus = !!activeElement && element.contains(activeElement)
  const hadKeyboardFocus = hadFocus && isKeyboardFocus(activeElement)
  flashAttribute('data-morphed', MORPHED_MS)
  nextTick(() => {
    writeTimerVariables()
    const currentElement = cardRef.value
    if(!hadFocus || !currentElement || isLeaving.value) {
      return
    }
    if(hadKeyboardFocus) {
      getFocusTarget(currentElement).focus({preventScroll: true})
      return
    }
    const currentActiveElement = getActiveElement()
    if(currentActiveElement && currentElement.contains(currentActiveElement)) {
      currentActiveElement.blur()
    }
    setFocused(false)
  })
}

function onStoreEvent(event: ILocalMessageStoreEvent) {
  if(event.type === LocalMessageStoreEventType.Tick) {
    writeTimerVariables()
    return
  }
  if(event.id !== props.message.id) {
    return
  }
  if(event.type === LocalMessageStoreEventType.Bump) {
    flashAttribute('data-bumped', BUMPED_MS)
    writeTimerVariables()
    return
  }
  onMorph()
}

function onEscape(event: KeyboardEvent) {
  const canClose = props.message.dismissible && props.message.severity !== MessageSeverityType.Progress
  if(event.defaultPrevented || isLeaving.value || !canClose) {
    return
  }
  // the card handles Esc before document listeners (open modal, keyboard shortcut store) see it
  event.preventDefault()
  event.stopPropagation()
  localMessageStore.dismiss(props.message.id, MessageDismissReasonType.Esc)
}

function onCardKeydown(event: KeyboardEvent) {
  if(event.key === 'Escape') {
    onEscape(event)
  }
}

function onActionClick(event: MouseEvent, action: IMessageAction, index: number) {
  if(action.to) {
    const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
    if(!isModifiedClick) {
      event.preventDefault()
      router.push(action.to)
    }
  } else if(!action.href || action.href === '#') {
    event.preventDefault()
  }
  localMessageStore.runAction(props.message.id, index)
}

function onCloseClick() {
  localMessageStore.dismiss(props.message.id, MessageDismissReasonType.Close)
}

// "Copied" is shown and announced only after a copy that really happened
async function onCopyClick() {
  const errorId = props.message.errorId
  if(!errorId) {
    return
  }
  const hasCopied = await writeToClipboard(errorId)
  copied.value = hasCopied
  if(hasCopied) {
    startCopiedReset()
  }
  localMessageStore.announce(
    MessageAnnouncementChannelType.Polite,
    hasCopied ? t('notifications.messages.copiedAnnouncement') : t('notifications.messages.copyFailedAnnouncement'),
  )
}

function onTransitionEnd(event: TransitionEvent) {
  // a child's transition (button colour) must not cut the card's own exit short
  if(event.target !== cardRef.value || !isLeaving.value) {
    return
  }
  localMessageStore.finishLeave(props.message.id)
}

onMounted(() => {
  const activeElement = getActiveElement()
  if(activeElement && activeElement !== document.body && !activeElement.closest('#messages-root')) {
    returnFocusElement = activeElement
  }
  unsubscribeFromStore = localMessageStore.subscribeToEvents(onStoreEvent)
  writeTimerVariables()
  if(props.message.state === MessageStateType.Enter) {
    const id = props.message.id
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        localMessageStore.markVisible(id)
      })
    })
  }
})

onBeforeUnmount(() => {
  unsubscribeFromStore?.()
  flashTimeouts.forEach(timeout => clearTimeout(timeout))
  flashTimeouts.clear()
  // clear() removes cards without the leave step, so the focus is handed back here
  const element = cardRef.value
  const activeElement = getActiveElement()
  if(element && activeElement && element.contains(activeElement)) {
    if(returnFocusElement?.isConnected) {
      returnFocusElement.focus({preventScroll: true})
    } else {
      activeElement.blur()
    }
  }
})
</script>

<template>
  <div
    ref="cardRef"
    class="msg"
    :data-cy="CypressUniqueNameType.MessageCard"
    :data-msg-id="message.id"
    :data-severity="message.severity"
    data-kind="local"
    :data-count="message.count"
    :data-state="message.state"
    :data-persistent="flag(message.duration === 0)"
    :data-pinned="flag(message.pinned)"
    :data-timer-bar="flag(message.timerBar && message.duration > 0)"
    :data-has-title="flag(!!message.title)"
    :data-actions="message.actions.length"
    :data-collapsed="flag(message.collapsed)"
    :data-paused="flag(isVisiblyPaused)"
    role="group"
    :aria-labelledby="labelledBy"
    :aria-hidden="isLeaving ? 'true' : undefined"
    tabindex="-1"
    :style="cardStyle"
    data-overlay-ignore
    @keydown="onCardKeydown"
    @pointerenter="onPointerEnter"
    @pointerleave="onPointerLeave"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
    @pointerdown="onPointerDown"
    @pointerup="onPointerEnd"
    @pointercancel="onPointerEnd"
    @transitionend="onTransitionEnd"
  >
    <span class="msg__rail" aria-hidden="true"/>
    <span
      class="msg__icon"
      :data-icon="message.severity"
      aria-hidden="true"
    >
      <IconComponent
        :name="MessageSeverityTypeToIconNameTypeMap[message.severity]"
        :size="SizeVariantType.Medium"
        :color="null"
        :behavior="isProgress ? IconBehavior.Spin : IconBehavior.None"
      />
    </span>
    <div class="msg__body">
      <div
        v-if="message.title"
        :id="titleElementId"
        class="msg__title"
      >
        {{message.title}}
      </div>
      <div
        :id="textElementId"
        class="msg__text"
        :data-clamped="message.clampLines ? '1' : undefined"
        :data-expanded="message.clampLines ? flag(isTextExpanded) : undefined"
      >
        {{message.text}}
      </div>
      <div v-if="hasMeta" class="msg__meta">
        <template v-if="message.errorId">
          <code :id="errorIdElementId" class="msg__error-id">{{message.errorId}}</code>
          <button
            type="button"
            class="msg__copy"
            :data-copy="message.errorId"
            :data-copied="copied ? '1' : undefined"
            :aria-describedby="errorIdElementId"
            @click.stop="onCopyClick"
          >
            <IconComponent :name="IconNameType.Copy" :size="SizeVariantType.XSmall" :color="null"/>
            <span>{{copied ? t('database.copied') : t('notifications.messages.copyId')}}</span>
          </button>
        </template>
        <button
          v-if="message.clampLines"
          type="button"
          class="msg__expand"
          :aria-expanded="isTextExpanded ? 'true' : 'false'"
          :aria-controls="textElementId"
          @click.stop="isTextExpanded = !isTextExpanded"
        >
          {{isTextExpanded ? t('database.showLess') : t('general.showMore')}}
        </button>
        <time v-if="timeLabel" class="msg__time" :datetime="createdAtIso">
          <IconComponent :name="IconNameType.Clock" :size="SizeVariantType.XSmall" :color="null"/>{{timeLabel}}
        </time>
      </div>
    </div>
    <div v-if="message.progress" class="msg__progress">
      <div
        class="msg__progress-bar"
        role="progressbar"
        :aria-labelledby="message.title ? titleElementId : textElementId"
        aria-valuemin="0"
        :aria-valuemax="message.progress.total ?? 100"
        :aria-valuenow="message.progress.indeterminate ? undefined : message.progress.value"
        :aria-valuetext="message.progress.label || undefined"
        :data-indeterminate="message.progress.indeterminate ? '1' : undefined"
      >
        <div class="msg__progress-fill" :style="{'--msg-progress-width': `${progressPercent}%`}"/>
      </div>
      <div class="msg__progress-label">{{message.progress.label ?? ''}}</div>
    </div>
    <div v-if="message.actions.length" class="msg__actions">
      <template v-for="item in actionItems" :key="item.index">
        <a
          v-if="item.href"
          class="msg__btn"
          :data-kind="item.action.kind"
          :data-action-index="item.index"
          :data-cy="item.action.dataCy ?? CypressUniqueNameType.MessageAction"
          :title="item.action.tooltip"
          :href="item.href"
          @click.stop="onActionClick($event, item.action, item.index)"
        >
          <IconComponent
            v-if="item.action.kind === MessageActionKindType.Undo"
            :name="IconNameType.Undo"
            :size="SizeVariantType.XSmall"
            :color="null"
          />{{item.action.label}}
        </a>
        <button
          v-else
          type="button"
          class="msg__btn"
          :data-kind="item.action.kind"
          :data-action-index="item.index"
          :data-cy="item.action.dataCy ?? CypressUniqueNameType.MessageAction"
          :title="item.action.tooltip"
          @click.stop="onActionClick($event, item.action, item.index)"
        >
          <IconComponent
            v-if="item.action.kind === MessageActionKindType.Undo"
            :name="IconNameType.Undo"
            :size="SizeVariantType.XSmall"
            :color="null"
          />{{item.action.label}}
        </button>
      </template>
    </div>
    <button
      v-if="message.dismissible"
      type="button"
      class="msg__close"
      data-kind="close"
      :aria-label="t('general.close')"
      @click.stop="onCloseClick"
    >
      <IconComponent :name="IconNameType.Xmark" :size="SizeVariantType.Medium" :color="null"/>
    </button>
    <span class="msg__count" aria-hidden="true">{{countLabel}}</span>
  </div>
</template>
